import React, { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { formatPhoneWithCountry } from "../../utils/countries";
import { toast } from "sonner";
import {
  Building2,
  Package,
  FileText,
  CheckCircle2,
  XCircle,
  Trash2,
  Plus,
  Pencil,
  Save,
  X,
  UserCheck,
  Radar,
  AlertCircle,
} from "lucide-react";
import { stageConfig, activePipelineStages, API_URL } from "./constants";
import { useAuth } from "../../contexts/AuthContext";
import CoverageSearchEmbed, { EMPTY_COVERAGE } from "../coverage/CoverageSearchEmbed";

const calcServiceMRR = (services) => {
  if (!services?.length) return 0;
  return services.reduce((total, svc) => {
    const qty = parseInt(svc.cantidad) || 1;
    return total + (svc.precio * qty) - (svc.descuento || 0);
  }, 0);
};

export const OpportunityDetailDialog = ({
  open,
  onOpenChange,
  selectedOpp,
  setSelectedOpp,
  customers,
  users,
  formatCurrency,
  formatDate,
  pendingStageChange,
  setPendingStageChange,
  stageChangeNote,
  setStageChangeNote,
  onStageChangeWithNote,
  newNoteText,
  setNewNoteText,
  onAddNote,
  onPrepareConversion,
  onOpenLose,
  onOpenQuoteModal,
  canDelete,
  canEditCustomers,
  onOpenDeleteConfirm,
  onUpdateProximaAccion,
  onUpdateEjecutivo,
  onRefresh,
}) => {
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [customerEdit, setCustomerEdit] = useState({});
  const [savingCustomer, setSavingCustomer] = useState(false);

  // Coverage modal state.
  //
  // Primary source of truth: the Likes iframe DOES emit postMessage events
  // with the documented payload {type:"coverage", status, address, vertical,
  // id}. `CoverageSearchEmbed` validates the origin, normalizes it (id →
  // gescal) and surfaces it via `onResult` → `coverageDraft`.
  //
  // Manual controls below stay available as a FALLBACK only for the rare
  // case where postMessage never arrives (e.g. provider misfires or the
  // user manually types an address that the iframe lookup never confirmed).
  const { canEdit } = useAuth();
  const canEditOpp = canEdit ? canEdit("pipeline") : true;
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [coverageDraft, setCoverageDraft] = useState(EMPTY_COVERAGE);
  // Structured manual address: required (street/postal_code/city) + optional
  // (portal/floor/door/notes). The composed address sent to the backend is
  // derived from these parts when the user goes through the manual flow.
  const emptyManualParts = {
    street: "", postal_code: "", city: "",
    portal: "", floor: "", door: "", notes: "",
  };
  const [manualParts, setManualParts] = useState(emptyManualParts);
  const [manualStatus, setManualStatus] = useState(""); // "available" | "unavailable" | "error"
  const [savingCoverage, setSavingCoverage] = useState(false);

  // Compose a display address string from structured parts.
  //   "Calle X 123, Portal B, Piso 3, Puerta 2A, 28001 Madrid"
  const composeAddress = (p) => {
    const base = [];
    if (p.street?.trim()) base.push(p.street.trim());
    if (p.portal?.trim()) base.push(`Portal ${p.portal.trim()}`);
    if (p.floor?.trim()) base.push(`Piso ${p.floor.trim()}`);
    if (p.door?.trim()) base.push(`Puerta ${p.door.trim()}`);
    const tail = [p.postal_code?.trim(), p.city?.trim()].filter(Boolean).join(" ");
    if (tail) base.push(tail);
    return base.join(", ");
  };

  // Convenience: did the iframe give us a valid official result in this
  // session? This drives both the save button and the fallback panel UI.
  const officialStatus = coverageDraft?.status || null; // "available"|"unavailable"|"error"|null
  const officialAddressMissing =
    officialStatus === "available" && !(coverageDraft?.address || "").trim();

  const openCoverage = () => {
    setCoverageDraft(EMPTY_COVERAGE);
    setManualParts(emptyManualParts);
    setManualStatus("");
    setCoverageOpen(true);
  };

  const saveCoverage = async () => {
    if (!selectedOpp) return;

    // Decide payload: official postMessage wins; manual is the fallback.
    let payload;
    if (officialStatus) {
      // Official branch. For `available` we prefer the provider address, but
      // accept a manual correction when the provider omitted it.
      const addr =
        officialStatus === "available"
          ? (coverageDraft.address || manualParts.street || "").trim()
          : "";
      if (officialStatus === "available" && !addr) return; // guarded by UI
      payload = {
        status: officialStatus,
        address: addr,
        vertical: coverageDraft.vertical || "",
        gescal: coverageDraft.gescal || "",
      };
    } else {
      // Manual branch — compose address from structured parts.
      if (!manualStatus) return;
      if (manualStatus === "available") {
        const requiredOk = !!manualParts.street.trim() &&
          !!manualParts.postal_code.trim() && !!manualParts.city.trim();
        if (!requiredOk) return; // guarded by UI
      }
      const composed = composeAddress(manualParts);
      payload = {
        status: manualStatus,
        address: manualStatus === "available" ? composed : "",
        vertical: "",
        gescal: "",
        // Only persist structured parts when they carry install-address
        // meaning. Backend drops them on unavailable/error.
        address_parts: manualStatus === "available" ? manualParts : null,
      };
    }

    setSavingCoverage(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/opportunities/${selectedOpp.id}/coverage-result`,
        payload,
      );
      // Optimistically update the dialog state so the panel refreshes without
      // a round-trip. The parent list refreshes on onRefresh().
      if (setSelectedOpp) {
        setSelectedOpp({ ...selectedOpp, coverage_result: res.data.coverage_result });
      }
      toast.success("Cobertura guardada en la oportunidad");
      setCoverageOpen(false);
      if (typeof onRefresh === "function") onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar cobertura");
    } finally {
      setSavingCoverage(false);
    }
  };

  // Save button enabled when EITHER we have a valid official result OR we
  // have a valid manual capture.
  const manualRequiredOk =
    !!manualParts.street.trim() &&
    !!manualParts.postal_code.trim() &&
    !!manualParts.city.trim();
  const canSaveOfficial =
    (officialStatus === "unavailable" || officialStatus === "error") ||
    (officialStatus === "available" &&
      !!((coverageDraft.address || manualParts.street).trim()));
  const canSaveManual =
    !officialStatus && !!manualStatus &&
    (manualStatus !== "available" || manualRequiredOk);
  const canSave = canEditOpp && !savingCoverage && (canSaveOfficial || canSaveManual);

  const startEditCustomer = (customer) => {
    setCustomerEdit({
      nombre: customer.nombre || "",
      apellidos: customer.apellidos || "",
      razon_social: customer.razon_social || "",
      cif: customer.cif || "",
      dni_nie: customer.dni_nie || "",
      // New explicit representante fields; fall back to legacy `dni_responsable`
      // so existing customer records still populate the form on first edit.
      representante_nombre: customer.representante_nombre || "",
      dni_representante: customer.dni_representante || customer.dni_responsable || "",
      persona_contacto: customer.persona_contacto || "",
      email: customer.email || "",
      phone: customer.phone || "",
      iban: customer.iban || "",
    });
    setEditingCustomer(true);
  };

  const saveCustomerEdit = async (customerId) => {
    setSavingCustomer(true);
    try {
      await axios.put(`${API_URL}/api/customers/${customerId}`, customerEdit);
      toast.success("Datos del cliente actualizados");
      setEditingCustomer(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error("Error al actualizar cliente");
    } finally {
      setSavingCustomer(false);
    }
  };

  return (
  <>
  <Dialog open={open} onOpenChange={(v) => { if (!v) setEditingCustomer(false); onOpenChange(v); }}>
    <DialogContent className="bg-[#0F0F3D] border-[#1E1E5F] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-xl text-white flex items-center gap-2">
          <span className="text-[#42E695]">{selectedOpp?.opportunity_id}</span>
        </DialogTitle>
      </DialogHeader>
      
      {selectedOpp && (
        <div className="space-y-4 mt-4">
          {/* Stage selector */}
          {!["GANADA", "PERDIDA"].includes(selectedOpp.stage) && (
            <div className="flex flex-col gap-3 p-3 bg-[#1B1B4B]/50 rounded-lg">
              <div className="flex items-center gap-4">
                <span className="text-slate-400 whitespace-nowrap">Etapa:</span>
                <Select value={selectedOpp.stage} onValueChange={(newStage) => { if (newStage !== selectedOpp.stage) { setPendingStageChange(newStage); setStageChangeNote(""); } }}>
                  <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white w-48"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                    {activePipelineStages.map((key) => (
                      <SelectItem key={key} value={key} className="text-white">
                        <div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${stageConfig[key].color}`}></span>{stageConfig[key].label}</div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {pendingStageChange && (
                <div className="flex flex-col gap-2 p-3 bg-[#0A0A35] rounded-lg border border-[#1E1E5F]">
                  <p className="text-sm text-slate-300">
                    Cambiar de <Badge className={`${stageConfig[selectedOpp.stage]?.color} text-white`}>{stageConfig[selectedOpp.stage]?.label}</Badge>
                    {" "}a{" "}
                    <Badge className={`${stageConfig[pendingStageChange]?.color} text-white`}>{stageConfig[pendingStageChange]?.label}</Badge>
                  </p>
                  <Textarea value={stageChangeNote} onChange={(e) => setStageChangeNote(e.target.value)} placeholder="Nota sobre este cambio de etapa (opcional)" className="bg-[#1B1B4B] border-[#1E1E5F] text-white text-sm min-h-[60px]" data-testid="stage-change-note" />
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setPendingStageChange(null)} className="border-[#1E1E5F] text-slate-300 h-8">Cancelar</Button>
                    <Button size="sm" onClick={onStageChangeWithNote} className="bg-[#42E695] text-[#04042C] h-8" data-testid="confirm-stage-change-btn">Confirmar cambio</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Opportunity details */}
          <div className="bg-[#1B1B4B] rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-3">{selectedOpp.title}</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-slate-400">MRR Estimado:</span><span className="text-[#42E695] ml-2 font-semibold">{formatCurrency(calcServiceMRR(selectedOpp.selected_services) || selectedOpp.estimated_mrr)}/mes</span></div>
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Ejecutivo:</span>
                <Select value={selectedOpp.ejecutivo_id || ""} onValueChange={onUpdateEjecutivo}>
                  <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white w-44 h-8" data-testid="ejecutivo-select">
                    <SelectValue placeholder="Sin asignar" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                    {(users || []).map((u) => (
                      <SelectItem key={u.id} value={u.id} className="text-white">{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2" data-testid="responsable-info-detail">
                <UserCheck className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-slate-400">Responsable:</span>
                <span className="text-white">{selectedOpp.responsable_nombre || "Sin responsable"}</span>
              </div>
              <div><span className="text-slate-400">Fecha Est. Cierre:</span><span className="text-white ml-2">{formatDate(selectedOpp.fecha_estimada_cierre)}</span></div>
              <div className="col-span-2 flex items-center gap-2">
                <span className="text-slate-400">Próxima Acción:</span>
                <Select value={selectedOpp.proxima_accion || ""} onValueChange={onUpdateProximaAccion}>
                  <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-[#03BEE4] w-56 h-8" data-testid="proxima-accion-select"><SelectValue placeholder="Seleccionar acción" /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                    <SelectItem value="Llamada telefónica" className="text-white">Llamada telefónica</SelectItem>
                    <SelectItem value="Visita presencial" className="text-white">Visita presencial</SelectItem>
                    <SelectItem value="Enviar propuesta" className="text-white">Enviar propuesta</SelectItem>
                    <SelectItem value="Presentación técnica" className="text-white">Presentación técnica</SelectItem>
                    <SelectItem value="Negociación de precio" className="text-white">Negociación de precio</SelectItem>
                    <SelectItem value="Firma de contrato" className="text-white">Firma de contrato</SelectItem>
                    <SelectItem value="Seguimiento" className="text-white">Seguimiento</SelectItem>
                    <SelectItem value="Otro" className="text-white">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Customer details */}
          <div className="bg-[#1B1B4B] rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-[#03BEE4] font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Datos del Cliente
              </h4>
              {canEditCustomers && !editingCustomer && (
                <Button variant="ghost" size="sm" onClick={() => {
                  const c = customers.find(c => c.id === selectedOpp.customer_id);
                  if (c) startEditCustomer(c);
                }} className="text-[#03BEE4] hover:bg-[#03BEE4]/10 h-7 px-2" data-testid="edit-customer-btn">
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                </Button>
              )}
            </div>
            {(() => {
              const customer = customers.find(c => c.id === selectedOpp.customer_id);
              if (!customer) return <p className="text-slate-400">Cliente no encontrado</p>;
              
              if (editingCustomer) {
                const isEmpresa = customer.customer_type === "EMPRESA";
                return (
                  <div className="space-y-3">
                    {isEmpresa ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div><Label className="text-slate-400 text-xs">Razón Social</Label><Input value={customerEdit.razon_social} onChange={e => setCustomerEdit({...customerEdit, razon_social: e.target.value})} className="h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm" /></div>
                        <div><Label className="text-slate-400 text-xs">CIF</Label><Input value={customerEdit.cif} onChange={e => setCustomerEdit({...customerEdit, cif: e.target.value})} className="h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm" /></div>
                        <div><Label className="text-slate-400 text-xs">Nombre del representante</Label><Input value={customerEdit.representante_nombre || ""} onChange={e => setCustomerEdit({...customerEdit, representante_nombre: e.target.value})} className="h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm" /></div>
                        <div><Label className="text-slate-400 text-xs">DNI/NIF del representante</Label><Input value={customerEdit.dni_representante || ""} onChange={e => setCustomerEdit({...customerEdit, dni_representante: e.target.value})} className="h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm" /></div>
                        <div><Label className="text-slate-400 text-xs">Persona de Contacto</Label><Input value={customerEdit.persona_contacto} onChange={e => setCustomerEdit({...customerEdit, persona_contacto: e.target.value})} className="h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm" /></div>
                        <div><Label className="text-slate-400 text-xs">Teléfono</Label><Input value={customerEdit.phone} onChange={e => setCustomerEdit({...customerEdit, phone: e.target.value})} className="h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm" /></div>
                        <div><Label className="text-slate-400 text-xs">Email</Label><Input value={customerEdit.email} onChange={e => setCustomerEdit({...customerEdit, email: e.target.value})} className="h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm" /></div>
                        <div><Label className="text-slate-400 text-xs">IBAN</Label><Input value={customerEdit.iban} onChange={e => setCustomerEdit({...customerEdit, iban: e.target.value})} placeholder="ES00 0000 0000 0000 0000 0000" className="h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm" /></div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label className="text-slate-400 text-xs">Nombre</Label><Input value={customerEdit.nombre} onChange={e => setCustomerEdit({...customerEdit, nombre: e.target.value})} className="h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm" /></div>
                          <div><Label className="text-slate-400 text-xs">Apellidos</Label><Input value={customerEdit.apellidos} onChange={e => setCustomerEdit({...customerEdit, apellidos: e.target.value})} className="h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm" /></div>
                          <div><Label className="text-slate-400 text-xs">DNI/NIE</Label><Input value={customerEdit.dni_nie} onChange={e => setCustomerEdit({...customerEdit, dni_nie: e.target.value})} className="h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label className="text-slate-400 text-xs">Email</Label><Input value={customerEdit.email} onChange={e => setCustomerEdit({...customerEdit, email: e.target.value})} className="h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm" /></div>
                          <div><Label className="text-slate-400 text-xs">Teléfono</Label><Input value={customerEdit.phone} onChange={e => setCustomerEdit({...customerEdit, phone: e.target.value})} className="h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm" /></div>
                          <div className="col-span-2"><Label className="text-slate-400 text-xs">IBAN</Label><Input value={customerEdit.iban} onChange={e => setCustomerEdit({...customerEdit, iban: e.target.value})} placeholder="ES00 0000 0000 0000 0000 0000" className="h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm" /></div>
                        </div>
                      </>
                    )}
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => setEditingCustomer(false)} className="border-[#1E1E5F] text-slate-300 h-7"><X className="h-3.5 w-3.5 mr-1" />Cancelar</Button>
                      <Button size="sm" onClick={() => saveCustomerEdit(customer.id)} disabled={savingCustomer} className="bg-[#42E695] text-[#04042C] h-7" data-testid="save-customer-btn"><Save className="h-3.5 w-3.5 mr-1" />Guardar</Button>
                    </div>
                  </div>
                );
              }
              
              return (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-400">Nombre:</span><span className="text-white ml-2">{selectedOpp.customer_name}</span></div>
                  <div><span className="text-slate-400">ID:</span><span className="text-white ml-2">{customer.customer_id}</span></div>
                  {customer.customer_type === "EMPRESA" ? (
                    <>
                      <div><span className="text-slate-400">CIF/NIF:</span><span className="text-white ml-2">{customer.cif || "-"}</span></div>
                      <div><span className="text-slate-400">Contacto:</span><span className="text-white ml-2">{customer.persona_contacto || "-"}</span></div>
                      <div><span className="text-slate-400">Nombre representante:</span><span className="text-white ml-2">{customer.representante_nombre || "-"}</span></div>
                      <div>
                        <span className="text-slate-400">
                          {customer.representante_document_type === "NIE"
                            ? "NIE representante"
                            : customer.representante_document_type === "PASAPORTE"
                              ? "Pasaporte representante"
                              : customer.representante_document_type === "DNI"
                                ? "DNI representante"
                                : "Documento representante"}:
                        </span>
                        <span className="text-white ml-2">{customer.dni_representante || customer.dni_responsable || "-"}</span>
                      </div>
                    </>
                  ) : (
                    <div>
                      <span className="text-slate-400">
                        {customer.document_type === "NIE" ? "NIE" : customer.document_type === "PASAPORTE" ? "Pasaporte" : customer.document_type === "DNI" ? "DNI" : "Documento"}:
                      </span>
                      <span className="text-white ml-2">{customer.dni_nie || "-"}</span>
                    </div>
                  )}
                  <div><span className="text-slate-400">Email:</span><span className="text-white ml-2">{customer.email || "-"}</span></div>
                  <div><span className="text-slate-400">Teléfono:</span><span className="text-white ml-2">{formatPhoneWithCountry(customer.phone, customer.phone_country) || "-"}</span></div>
                  <div><span className="text-slate-400">IBAN:</span><span className="text-white ml-2">{customer.iban || "-"}</span></div>
                </div>
              );
            })()}
          </div>
          
          {/* Fiber coverage (optional snapshot on the opportunity) */}
          <div className="bg-[#1B1B4B] rounded-lg p-4" data-testid="opp-coverage-section">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-[#03BEE4] font-semibold flex items-center gap-2">
                <Radar className="h-4 w-4" />
                Cobertura Fibra
              </h4>
              {canEditOpp && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={openCoverage}
                  className="border-[#1E1E5F] text-[#03BEE4] hover:bg-[#03BEE4]/10 h-8 text-xs"
                  data-testid="opp-open-coverage-btn"
                >
                  <Radar className="h-3.5 w-3.5 mr-1" />
                  {selectedOpp.coverage_result ? "Actualizar cobertura" : "Consultar cobertura"}
                </Button>
              )}
            </div>

            {!selectedOpp.coverage_result ? (
              <p className="text-sm text-slate-400">Sin cobertura asociada.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs uppercase text-slate-500">Estado</div>
                  {selectedOpp.coverage_result.status === "available" && (
                    <Badge className="bg-[#42E695]/20 text-[#42E695] border border-[#42E695]/40 mt-1">
                      Cobertura disponible
                    </Badge>
                  )}
                  {selectedOpp.coverage_result.status === "unavailable" && (
                    <Badge className="bg-slate-600/20 text-slate-300 border border-slate-500/40 mt-1">
                      Sin cobertura
                    </Badge>
                  )}
                  {selectedOpp.coverage_result.status === "error" && (
                    <Badge className="bg-amber-500/20 text-amber-300 border border-amber-400/40 mt-1">
                      No se pudo confirmar
                    </Badge>
                  )}
                </div>
                <div>
                  <div className="text-xs uppercase text-slate-500">Dirección consultada</div>
                  <div className="text-white break-words" data-testid="opp-coverage-address">
                    {selectedOpp.coverage_result.address || "—"}
                  </div>
                </div>
                {/* Vertical / GESCAL only surface if a previous postMessage
                    result populated them. Never customer-facing. */}
                {selectedOpp.coverage_result.vertical && (
                  <div>
                    <div className="text-xs uppercase text-slate-500">Vertical</div>
                    <div className="text-white break-words">{selectedOpp.coverage_result.vertical}</div>
                  </div>
                )}
                {selectedOpp.coverage_result.gescal && (
                  <div>
                    <div className="text-xs uppercase text-slate-500">GESCAL / ID</div>
                    <div className="text-white font-mono text-xs break-all">
                      {selectedOpp.coverage_result.gescal}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-xs uppercase text-slate-500">Fecha consulta</div>
                  <div className="text-white">{formatDate ? formatDate(selectedOpp.coverage_result.checked_at) : (selectedOpp.coverage_result.checked_at || "—")}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-slate-500">Consultado por</div>
                  <div className="text-white">{selectedOpp.coverage_result.checked_by_name || "—"}</div>
                </div>
              </div>
            )}
          </div>

          {/* Selected Services with discounts */}
          {selectedOpp.selected_services?.length > 0 && (
            <div className="bg-[#1B1B4B] rounded-lg p-4">
              <h4 className="text-[#03BEE4] font-semibold mb-3 flex items-center gap-2">
                <Package className="h-4 w-4" />
                Servicios ({selectedOpp.selected_services.length})
              </h4>
              <div className="space-y-1">
                {(() => {
                  const svcs = selectedOpp.selected_services;
                  const hasBloque = svcs.some(s => s.bloque);
                  
                  const renderServiceLine = (svc, idx) => {
                    const qty = parseInt(svc.cantidad) || 1;
                    const discount = svc.descuento || 0;
                    return (
                      <div key={idx}>
                        <div className="flex justify-between text-sm">
                          <span className={`${(svc.precio || 0) < 0 ? "text-red-400" : "text-white"}`}>
                            {svc.nombre}
                            {svc.linea_numero && <span className="text-slate-500 ml-1">(línea {svc.linea_numero})</span>}
                          </span>
                          <span className={`${(svc.precio || 0) < 0 ? "text-red-400" : "text-[#42E695]"}`}>
                            {formatCurrency(svc.precio)} x{qty}
                          </span>
                        </div>
                        {discount > 0 && (
                          <div className="flex justify-between text-xs pl-4">
                            <span className="text-red-400/80">Descuento aplicado:</span>
                            <span className="text-red-400">-{formatCurrency(discount)}</span>
                          </div>
                        )}
                      </div>
                    );
                  };
                  
                  if (!hasBloque) return svcs.map((svc, idx) => renderServiceLine(svc, idx));
                  
                  const groups = {};
                  const noBloque = [];
                  svcs.forEach(s => { if (s.bloque) { if (!groups[s.bloque]) groups[s.bloque] = []; groups[s.bloque].push(s); } else noBloque.push(s); });
                  const bloqueLabels = { PAQUETE_CONVERGENTE: "PAQUETE CONVERGENTE", MOVILES_ADICIONALES: "MOVILES ADICIONALES", SOLO_FIBRA: "SOLO FIBRA", SOLO_MOVIL: "SOLO MOVIL", TV: "TV", CENTRALITA: "CENTRALITA", WIFI_PYME: "WIFI PYME" };
                  return (
                    <>
                      {Object.entries(groups).map(([bloque, items]) => (
                        <div key={bloque} className="mb-3">
                          <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1">{bloqueLabels[bloque] || bloque}</p>
                          {items.map((svc, idx) => renderServiceLine(svc, `${bloque}-${idx}`))}
                        </div>
                      ))}
                      {noBloque.length > 0 && noBloque.map((svc, idx) => renderServiceLine(svc, `nb-${idx}`))}
                    </>
                  );
                })()}
              </div>
              <div className="flex justify-between pt-3 mt-3 border-t border-[#1E1E5F]">
                <span className="text-slate-300 font-semibold">Total MRR:</span>
                <span className="text-[#42E695] font-bold">{formatCurrency(calcServiceMRR(selectedOpp.selected_services))}/mes</span>
              </div>
            </div>
          )}
          
          {/* Notes History */}
          <div className="bg-[#1B1B4B] rounded-lg p-4">
            <h4 className="text-[#03BEE4] font-semibold mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Notas ({selectedOpp.notes_history?.length || (selectedOpp.notes ? 1 : 0)})
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
              {selectedOpp.notes_history?.length > 0 ? (
                [...selectedOpp.notes_history].reverse().map((note) => (
                  <div key={note.id} className={`p-2 rounded text-sm border-l-2 ${note.type === "stage_change" ? "bg-[#03BEE4]/10 border-[#03BEE4]" : "bg-[#0A0A35] border-[#42E695]"}`}>
                    <p className="text-slate-200">{note.text}</p>
                    <p className="text-xs text-slate-500 mt-1">{note.user_name} &middot; {new Date(note.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}</p>
                  </div>
                ))
              ) : selectedOpp.notes ? (
                <div className="p-2 rounded text-sm bg-[#0A0A35] border-l-2 border-[#42E695]">
                  <p className="text-slate-200">{selectedOpp.notes}</p>
                  <p className="text-xs text-slate-500 mt-1">Nota inicial</p>
                </div>
              ) : (
                <p className="text-slate-500 text-sm">Sin notas</p>
              )}
            </div>
            <div className="flex gap-2">
              <Textarea value={newNoteText} onChange={(e) => setNewNoteText(e.target.value)} placeholder="Añadir una nota..." className="bg-[#0A0A35] border-[#1E1E5F] text-white text-sm min-h-[40px] flex-1" data-testid="add-note-input" />
              <Button size="sm" onClick={onAddNote} disabled={!newNoteText.trim()} className="bg-[#42E695] text-[#04042C] self-end" data-testid="add-note-btn"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
          
          {selectedOpp.resultado && (
            <div className={`p-4 rounded-lg ${selectedOpp.resultado === "GANADO" ? "bg-[#42E695]/20" : "bg-red-500/20"}`}>
              <span className={selectedOpp.resultado === "GANADO" ? "text-[#42E695]" : "text-red-400"}>
                Resultado: <strong>{selectedOpp.resultado}</strong>
              </span>
              {selectedOpp.motivo_perdida && <p className="text-slate-300 mt-2">Motivo: {selectedOpp.motivo_perdida}</p>}
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-[#1E1E5F]">
            {!["GANADA", "PERDIDA"].includes(selectedOpp.stage) && (
              <>
                <Button onClick={onPrepareConversion} className="bg-[#42E695] text-[#04042C]"><CheckCircle2 className="h-4 w-4 mr-2" />Marcar Ganada</Button>
                <Button onClick={onOpenLose} className="bg-red-500/20 text-red-400 hover:bg-red-500/30"><XCircle className="h-4 w-4 mr-2" />Marcar Perdida</Button>
              </>
            )}
            <Button variant="outline" onClick={onOpenQuoteModal} className="border-[#03BEE4] text-[#03BEE4] hover:bg-[#03BEE4]/20" data-testid="generate-quote-btn">
              <FileText className="h-4 w-4 mr-2" />Generar Presupuesto
            </Button>
            {selectedOpp.quote_pdf_url && (
              <a href={selectedOpp.quote_pdf_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#42E695] text-sm hover:underline">Ver último PDF</a>
            )}
            {canDelete && (
              <Button variant="outline" onClick={() => onOpenDeleteConfirm(selectedOpp.id)} className="border-red-500/50 text-red-400 hover:bg-red-500/20 ml-auto">
                <Trash2 className="h-4 w-4 mr-2" />Eliminar
              </Button>
            )}
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>

  {/* Coverage modal — separate Dialog so the iframe is unmounted on close */}
  <Dialog open={coverageOpen} onOpenChange={setCoverageOpen}>
    <DialogContent className="bg-[#0F0F3D] border-[#1E1E5F] text-white max-w-5xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-xl text-white flex items-center gap-2">
          <Radar className="h-5 w-5 text-[#03BEE4]" />
          Consultar Cobertura
        </DialogTitle>
      </DialogHeader>
      <div className="mt-3 space-y-4">
        <CoverageSearchEmbed
          initialResult={EMPTY_COVERAGE}
          onResult={setCoverageDraft}
          iframeHeight="550px"
        />

        {/*
          Capture layer.
          PRIMARY source of truth = postMessage from the provider iframe,
          normalized by CoverageSearchEmbed and delivered to `coverageDraft`.
          The manual fallback below is only exposed when no official result
          is captured (e.g. provider misfires or the user bypasses the
          iframe entirely).
        */}
        {officialStatus ? (
          <div
            className="rounded-lg border border-[#03BEE4]/40 bg-[#03BEE4]/5 p-4 space-y-3"
            data-testid="opp-coverage-official-capture"
          >
            <div className="flex items-center gap-2">
              <Radar className="h-4 w-4 text-[#03BEE4]" />
              <span className="text-sm font-semibold text-[#03BEE4]">
                Resultado capturado del buscador
              </span>
            </div>

            {officialStatus === "available" && (
              <div className="space-y-3 text-sm" data-testid="opp-coverage-official-available">
                <Badge className="bg-[#42E695]/20 text-[#42E695] border border-[#42E695]/40 w-fit">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                  Cobertura disponible
                </Badge>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase text-slate-500">Dirección</div>
                    <div className="text-white break-words">
                      {coverageDraft.address || <span className="text-amber-300">—</span>}
                    </div>
                  </div>
                  {coverageDraft.vertical && (
                    <div>
                      <div className="text-xs uppercase text-slate-500">Vertical</div>
                      <div className="text-white break-words">{coverageDraft.vertical}</div>
                    </div>
                  )}
                  {coverageDraft.gescal && (
                    <div className="md:col-span-2">
                      <div className="text-xs uppercase text-slate-500">ID interno</div>
                      <div className="text-white font-mono text-xs break-all">
                        {coverageDraft.gescal}
                      </div>
                    </div>
                  )}
                </div>

                {officialAddressMissing && (
                  <div
                    className="rounded-md border border-amber-400/40 bg-amber-500/10 p-3 space-y-2"
                    data-testid="opp-coverage-address-missing-warning"
                  >
                    <div className="flex items-start gap-2 text-xs text-amber-200">
                      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>
                        El buscador confirmó cobertura pero no devolvió la dirección.
                        Introdúcela manualmente para poder guardar.
                      </span>
                    </div>
                    <Input
                      value={manualParts.street}
                      onChange={(e) => setManualParts((p) => ({ ...p, street: e.target.value }))}
                      placeholder="Dirección consultada"
                      className="bg-[#0F0F3D] border-[#1E1E5F] text-white"
                      data-testid="opp-coverage-address-correction"
                    />
                  </div>
                )}
              </div>
            )}

            {officialStatus === "unavailable" && (
              <Badge
                className="bg-slate-500/30 text-slate-200 border border-slate-400/50 w-fit"
                data-testid="opp-coverage-official-unavailable"
              >
                <XCircle className="h-3.5 w-3.5 mr-1" />
                Sin cobertura disponible
              </Badge>
            )}

            {officialStatus === "error" && (
              <Badge
                className="bg-amber-500/20 text-amber-300 border border-amber-400/50 w-fit"
                data-testid="opp-coverage-official-error"
              >
                <AlertCircle className="h-3.5 w-3.5 mr-1" />
                No se pudo confirmar
              </Badge>
            )}
          </div>
        ) : (
          /* Manual capture — only visible when the iframe has not yet
             surfaced an official result. Collapsed by default so it does
             not compete with the primary (iframe) flow. */
          <details
            className="rounded-lg border border-[#1E1E5F] bg-[#0A0A35] overflow-hidden"
            data-testid="opp-coverage-manual-fallback"
          >
            <summary className="cursor-pointer select-none px-4 py-3 text-xs uppercase tracking-wide text-slate-400 hover:text-slate-200">
              Registrar resultado manualmente
            </summary>
            <div className="p-4 space-y-4 border-t border-[#1E1E5F]">
              <p className="text-xs text-slate-500">
                Úsalo solo si el buscador no devuelve el resultado automáticamente.
              </p>

              {/* Structured address — required fields for "available". */}
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                <div className="md:col-span-6">
                  <Label htmlFor="opp-cov-street" className="text-xs text-slate-400 mb-1 block">
                    Dirección / calle y número <span className="text-[#03BEE4]">*</span>
                  </Label>
                  <Input
                    id="opp-cov-street"
                    value={manualParts.street}
                    onChange={(e) => setManualParts((p) => ({ ...p, street: e.target.value }))}
                    placeholder="Ej: Calle Mayor 123"
                    className="bg-[#0F0F3D] border-[#1E1E5F] text-white"
                    data-testid="opp-coverage-address-manual"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="opp-cov-postal" className="text-xs text-slate-400 mb-1 block">
                    Código postal <span className="text-[#03BEE4]">*</span>
                  </Label>
                  <Input
                    id="opp-cov-postal"
                    value={manualParts.postal_code}
                    onChange={(e) => setManualParts((p) => ({ ...p, postal_code: e.target.value }))}
                    placeholder="28001"
                    className="bg-[#0F0F3D] border-[#1E1E5F] text-white"
                    data-testid="opp-coverage-postal"
                  />
                </div>
                <div className="md:col-span-4">
                  <Label htmlFor="opp-cov-city" className="text-xs text-slate-400 mb-1 block">
                    Ciudad / municipio <span className="text-[#03BEE4]">*</span>
                  </Label>
                  <Input
                    id="opp-cov-city"
                    value={manualParts.city}
                    onChange={(e) => setManualParts((p) => ({ ...p, city: e.target.value }))}
                    placeholder="Madrid"
                    className="bg-[#0F0F3D] border-[#1E1E5F] text-white"
                    data-testid="opp-coverage-city"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="opp-cov-portal" className="text-xs text-slate-400 mb-1 block">Portal</Label>
                  <Input
                    id="opp-cov-portal"
                    value={manualParts.portal}
                    onChange={(e) => setManualParts((p) => ({ ...p, portal: e.target.value }))}
                    placeholder="B"
                    className="bg-[#0F0F3D] border-[#1E1E5F] text-white"
                    data-testid="opp-coverage-portal"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="opp-cov-floor" className="text-xs text-slate-400 mb-1 block">Piso</Label>
                  <Input
                    id="opp-cov-floor"
                    value={manualParts.floor}
                    onChange={(e) => setManualParts((p) => ({ ...p, floor: e.target.value }))}
                    placeholder="3"
                    className="bg-[#0F0F3D] border-[#1E1E5F] text-white"
                    data-testid="opp-coverage-floor"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="opp-cov-door" className="text-xs text-slate-400 mb-1 block">Puerta</Label>
                  <Input
                    id="opp-cov-door"
                    value={manualParts.door}
                    onChange={(e) => setManualParts((p) => ({ ...p, door: e.target.value }))}
                    placeholder="2A"
                    className="bg-[#0F0F3D] border-[#1E1E5F] text-white"
                    data-testid="opp-coverage-door"
                  />
                </div>
                <div className="md:col-span-6">
                  <Label htmlFor="opp-cov-notes" className="text-xs text-slate-400 mb-1 block">
                    Observaciones dirección
                  </Label>
                  <Input
                    id="opp-cov-notes"
                    value={manualParts.notes}
                    onChange={(e) => setManualParts((p) => ({ ...p, notes: e.target.value }))}
                    placeholder="Referencias o indicaciones para el técnico"
                    className="bg-[#0F0F3D] border-[#1E1E5F] text-white"
                    data-testid="opp-coverage-notes"
                  />
                </div>
              </div>

              {/* Live preview of the composed address — reassures the user
                  what will be saved and later used to prefill GANADA. */}
              {(manualParts.street || manualParts.postal_code || manualParts.city) && (
                <div className="rounded-md bg-[#0F0F3D] border border-[#1E1E5F] p-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">
                    Dirección compuesta
                  </div>
                  <div className="text-sm text-slate-200 break-words" data-testid="opp-coverage-composed-preview">
                    {composeAddress(manualParts) || "—"}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-sm text-slate-300 mb-2 block">
                  Resultado de la comprobación
                </Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setManualStatus("available")}
                    className={`h-9 text-xs ${
                      manualStatus === "available"
                        ? "bg-[#42E695]/20 text-[#42E695] border-[#42E695]"
                        : "bg-transparent text-slate-300 border-[#1E1E5F] hover:bg-[#1E1E5F]/40"
                    }`}
                    data-testid="opp-coverage-status-available"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                    Cobertura disponible
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setManualStatus("unavailable")}
                    className={`h-9 text-xs ${
                      manualStatus === "unavailable"
                        ? "bg-slate-500/30 text-slate-200 border-slate-400"
                        : "bg-transparent text-slate-300 border-[#1E1E5F] hover:bg-[#1E1E5F]/40"
                    }`}
                    data-testid="opp-coverage-status-unavailable"
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1.5" />
                    Sin cobertura disponible
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setManualStatus("error")}
                    className={`h-9 text-xs ${
                      manualStatus === "error"
                        ? "bg-amber-500/20 text-amber-300 border-amber-400"
                        : "bg-transparent text-slate-300 border-[#1E1E5F] hover:bg-[#1E1E5F]/40"
                    }`}
                    data-testid="opp-coverage-status-error"
                  >
                    No se pudo confirmar
                  </Button>
                </div>
              </div>
            </div>
          </details>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-[#1E1E5F]">
          <div className="text-xs text-slate-500 space-y-1 max-w-md">
            <p>Cierra la ventana para descartar. Solo se guarda si pulsas el botón.</p>
            {!canSave && !savingCoverage && (
              <p className="text-amber-300/90" data-testid="opp-coverage-save-hint">
                {officialStatus === "available"
                  ? "El buscador no devolvió la dirección. Introdúcela para poder guardar."
                  : manualStatus === "available"
                  ? "Completa dirección, código postal y ciudad para guardar."
                  : "Usa el buscador o registra el resultado manualmente para poder guardar."}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setCoverageOpen(false)}
              className="border-[#1E1E5F] text-slate-300"
              data-testid="opp-coverage-cancel-btn"
            >
              Cerrar sin guardar
            </Button>
            <Button
              onClick={saveCoverage}
              disabled={!canSave}
              aria-disabled={!canSave}
              className={
                canSave
                  ? "bg-[#42E695] text-[#04042C] hover:bg-[#3ACC80]"
                  : "bg-[#1E1E5F] text-slate-500 border border-[#1E1E5F] cursor-not-allowed hover:bg-[#1E1E5F]"
              }
              data-testid="opp-coverage-save-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingCoverage ? "Guardando..." : "Guardar cobertura en oportunidad"}
            </Button>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
  </>
  );
};
